import { forwardRef, type Key } from 'react';
import { Autocomplete, AutocompleteItem, AutocompleteProps } from '@heroui/react';
import cn from 'classnames';
import styles from './CustomAutocomplete.module.scss';

interface CustomAutocompleteProps
  extends Omit<
    AutocompleteProps,
    'children' | 'items' | 'defaultItems' | 'selectedKey' | 'onSelectionChange' | 'errorMessage'
  > {
  items: { key: string; label: string }[];
  selectedKey?: string | null;
  onSelectionChange?: (key: string | null) => void;
  errorMessage?: string;
  showError?: boolean;
}

// Селект с поиском по label. Ошибка выводится в строке фиксированной высоты, чтобы поля не прыгали
export const CustomAutocomplete = forwardRef<HTMLInputElement, CustomAutocompleteProps>(
  (
    { items, selectedKey, onSelectionChange, errorMessage, showError = false, ...otherProps },
    ref,
  ) => {
    const isInvalid = !!errorMessage;

    return (
      <div className="w-full">
        <Autocomplete
          ref={ref}
          aria-label="Autocomplete"
          labelPlacement="outside"
          variant="bordered"
          radius="sm"
          defaultItems={items}
          selectedKey={selectedKey ?? null}
          onSelectionChange={(key: Key | null) =>
            onSelectionChange?.(key === null ? null : String(key))
          }
          isInvalid={isInvalid}
          popoverProps={{ shouldBlockScroll: false }}
          listboxProps={{ emptyContent: 'Nothing found', className: styles.listbox }}
          inputProps={{
            classNames: {
              label: cn(styles.label, isInvalid && styles.labelError),
              inputWrapper: cn(styles.inputWrapper, otherProps.isDisabled && styles.disabled),
              input: styles.input,
              helperWrapper: 'hidden',
            },
          }}
          classNames={{ popoverContent: styles.popoverContent }}
          {...otherProps}
        >
          {(item) => <AutocompleteItem key={item.key}>{item.label}</AutocompleteItem>}
        </Autocomplete>
        <p className="min-h-5 pt-1 text-tiny text-danger">{showError ? errorMessage : ''}</p>
      </div>
    );
  },
);

CustomAutocomplete.displayName = 'CustomAutocomplete';
